import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import { Permission } from '../../types/rbac.types';
import { Button, Badge } from '../ui/UIComponents';

interface PermissionTreeNode {
  id: string;
  name: string;
  children?: PermissionTreeNode[];
  data?: Permission;
  type: 'application' | 'category' | 'permission';
  icon?: React.ReactNode;
}

interface PermissionTreeViewProps {
  nodes: PermissionTreeNode[];
  onAddPermission: (permission: Permission) => void;
  onRemovePermission: (permission: Permission) => void;
  onAddPermissionsBulk?: (permissions: Permission[]) => void;
  onRemovePermissionsBulk?: (permissions: Permission[]) => void;
  disabled?: boolean;
  mode: 'add' | 'remove';
  searchTerm?: string;
}

const PermissionTreeItem: React.FC<{
  node: PermissionTreeNode;
  level: number;
  onAddPermission: (permission: Permission) => void;
  onRemovePermission: (permission: Permission) => void;
  onAddPermissionsBulk?: (permissions: Permission[]) => void;
  onRemovePermissionsBulk?: (permissions: Permission[]) => void;
  disabled?: boolean;
  mode: 'add' | 'remove';
  searchTerm?: string;
}> = ({ 
  node, 
  level, 
  onAddPermission, 
  onRemovePermission, 
  onAddPermissionsBulk,
  onRemovePermissionsBulk,
  disabled = false,
  mode,
  searchTerm = ''
}) => {
  const [expanded, setExpanded] = useState(searchTerm ? true : level < 1);
  const hasChildren = node.children && node.children.length > 0;
  
  // Function to collect all permission nodes from this node and its children
  const getAllChildPermissions = (node: PermissionTreeNode): Permission[] => {
    let permissions: Permission[] = [];
    
    if (node.type === 'permission' && node.data) {
      permissions.push(node.data);
    }
    
    if (node.children) {
      node.children.forEach(child => {
        permissions = [...permissions, ...getAllChildPermissions(child)];
      });
    }
    
    return permissions;
  };
  
  // Handle select all permissions in this node
  const handleSelectAllInNode = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    if (node.type !== 'permission') {
      const allPermissions = getAllChildPermissions(node);
      if (mode === 'add' && onAddPermissionsBulk) {
        onAddPermissionsBulk(allPermissions);
      } else if (mode === 'remove' && onRemovePermissionsBulk) {
        onRemovePermissionsBulk(allPermissions);
      } else {
        allPermissions.forEach(permission => {
          if (mode === 'add') {
            onAddPermission(permission);
          } else {
            onRemovePermission(permission);
          }
        });
      }
    }
  };
  
  // Check if this node or any of its children match the search term
  const matchesSearch = (node: PermissionTreeNode, term: string): boolean => {
    if (!term) return true;
    
    const termLower = term.toLowerCase();
    if (node.name.toLowerCase().includes(termLower)) return true;
    if (node.data?.code?.toLowerCase().includes(termLower)) return true;
    
    if (node.children) {
      return node.children.some(child => matchesSearch(child, term));
    }
    
    return false;
  };
  
  // If there's a search term and this node doesn't match, don't render it
  if (searchTerm && !matchesSearch(node, searchTerm)) {
    return null;
  }
  
  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };
  
  const handleAction = () => {
    if (node.type === 'permission' && node.data) {
      if (mode === 'add') {
        onAddPermission(node.data);
      } else {
        onRemovePermission(node.data);
      }
    }
  };
  
  // Determine background color based on node type
  const getBgColor = () => {
    if (node.type === 'application') return 'hover:bg-blue-900/20';
    if (node.type === 'category') return 'hover:bg-purple-900/20';
    return 'hover:bg-gray-800/40';
  };
  
  return (
    <div className="select-none">
      <div
        className={`flex items-center py-1.5 px-2 rounded-md cursor-pointer transition-colors ${getBgColor()}`}
        style={{ paddingLeft: `${(level + 1) * 12}px` }}
      >
        <div 
          className="mr-1.5 flex items-center justify-center w-5 h-5"
          onClick={hasChildren ? toggleExpand : undefined}
        >
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )
          ) : (
            <div className="w-4" />
          )}
        </div>
        
        {node.icon && <div className="mr-2">{node.icon}</div>}
        
        <div className="flex-1">
          <div className="flex items-center">
            <span className={`truncate ${node.type === 'application' ? 'font-medium text-blue-300' : node.type === 'category' ? 'text-purple-300' : 'text-gray-300'}`}>
              {node.name}
            </span>
            
            {node.data?.code && (
              <code className="ml-2 bg-gray-800/80 px-1.5 py-0.5 rounded text-xs text-indigo-300 font-mono border border-indigo-900/20">
                {node.data.code}
              </code>
            )}
          </div>
        </div>
        
        {(node.type === 'application' || node.type === 'category') && hasChildren && (
          <Button
            variant={mode === 'add' ? "outline" : "outline-danger"}
            size="xs"
            onClick={handleSelectAllInNode}
            disabled={disabled}
            className="ml-2 text-xs"
          >
            {mode === 'add' ? 'Select All' : 'Remove All'}
          </Button>
        )}
        
        {node.type === 'permission' && (
          <Button
            variant={mode === 'add' ? "outline" : "outline-danger"}
            size="xs"
            onClick={handleAction}
            disabled={disabled}
            className="ml-2"
          >
            {mode === 'add' ? (
              <>
                <Plus size={14} className="mr-1" />
                Add
              </>
            ) : (
              <>
                <X size={14} className="mr-1" />
                Remove
              </>
            )}
          </Button>
        )}
      </div>
      
      {expanded && hasChildren && (
        <div className="mt-0.5">
          {node.children!.map((child) => (
            <PermissionTreeItem
              key={child.id}
              node={child}
              level={level + 1}
              onAddPermission={onAddPermission}
              onRemovePermission={onRemovePermission}
              onAddPermissionsBulk={onAddPermissionsBulk}
              onRemovePermissionsBulk={onRemovePermissionsBulk}
              disabled={disabled}
              mode={mode}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PermissionTreeView: React.FC<PermissionTreeViewProps> = ({
  nodes,
  onAddPermission,
  onRemovePermission,
  onAddPermissionsBulk,
  onRemovePermissionsBulk,
  disabled = false,
  mode,
  searchTerm = ''
}) => {
  // Function to collect all permission nodes from the tree
  const getAllPermissions = (nodes: PermissionTreeNode[]): Permission[] => {
    let permissions: Permission[] = [];
    
    const traverse = (node: PermissionTreeNode) => {
      if (node.type === 'permission' && node.data) {
        permissions.push(node.data);
      }
      
      if (node.children) {
        node.children.forEach(traverse);
      }
    };
    
    nodes.forEach(traverse);
    return permissions;
  };
  
  // Handle select all permissions
  const handleSelectAll = () => {
    const allPermissions = getAllPermissions(nodes);
    if (mode === 'add' && onAddPermissionsBulk) {
      onAddPermissionsBulk(allPermissions);
    } else if (mode === 'remove' && onRemovePermissionsBulk) {
      onRemovePermissionsBulk(allPermissions);
    } else {
      allPermissions.forEach(permission => {
        if (mode === 'add') {
          onAddPermission(permission);
        } else {
          onRemovePermission(permission);
        }
      });
    }
  };
  
  return (
    <div className="overflow-y-auto max-h-[500px] bg-gray-900/50 rounded-md border border-gray-800">
      {nodes.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-400">No permissions available.</p>
        </div>
      ) : (
        <>
          <div className="p-2 border-b border-gray-700 flex justify-end">
            <Button
              variant={mode === 'add' ? "outline" : "outline-danger"}
              size="xs"
              onClick={handleSelectAll}
              disabled={disabled}
              className="text-xs"
            >
              {mode === 'add' ? 'Select All' : 'Remove All'}
            </Button>
          </div>
          <div className="p-2">
            {nodes.map((node) => (
              <PermissionTreeItem
                key={node.id}
                node={node}
                level={0}
                onAddPermission={onAddPermission}
                onRemovePermission={onRemovePermission}
                onAddPermissionsBulk={onAddPermissionsBulk}
                onRemovePermissionsBulk={onRemovePermissionsBulk}
                disabled={disabled}
                mode={mode}
                searchTerm={searchTerm}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PermissionTreeView;
export type { PermissionTreeNode };
